/**
 * Audit Log Viewer — Admin Page (DC-636, reworked v2.5 SS-04 / PR C3 to
 * the G0 §8 contract).
 *
 * One bound, one total: the first load takes the server's database-clock
 * `as_of` and threads it — verbatim — to the summary, every subsequent
 * page, and the export, so every number on this screen describes the same
 * snapshot. The caption names the bound and the org timezone (including a
 * visible fallback when the configured zone is invalid).
 *
 * Filters shown are filters applied: the draft/applied split is explicit,
 * editing without applying shows an unapplied notice, the summary receives
 * EVERY applied filter, and Clear all resets everything including dates.
 *
 * States (G0 §8.3): loaded / truncated / loading (previous counts
 * retained) / empty (the log is not empty — total named) / counts
 * unavailable ("Audit data is unavailable. Counts and export are
 * disabled.") / error (reference ID + Retry). Export is disabled in every
 * non-loaded state with the reason on the control's accessible name, and
 * goes through a real modal confirmation (C2 dialog contract) disclosing
 * bound, totals, truncation, and the audit_exported self-record.
 *
 * Pagination is keyset (next_cursor from the server) — Prev replays the
 * client-side cursor stack; equal timestamps can never shuffle pages.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import './audit-log-viewer.css';

// =============================================================================
// TYPES
// =============================================================================

interface AuditLogEntry {
  id: string;
  org_id: string;
  user_id: string | null;
  user_email: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

interface AuditLogFilter {
  action?: string;
  resource_type?: string;
  /** Actor "Name or ID" search (G0) — exact user id, or substring over the
   *  stored actor email / actorName snapshot; matching is server-side. */
  actor?: string;
  start_date?: string;
  end_date?: string;
}

interface Cursor {
  created_at: string;
  id: string;
}

interface TimezoneInfo {
  timezone: string;
  timezone_source: 'org_setting' | 'default' | 'invalid_setting';
  timezone_invalid_value?: string;
}

interface LogsPayload extends TimezoneInfo {
  entries: AuditLogEntry[];
  pagination: { total: number; limit: number; next_cursor: Cursor | null };
  as_of: string;
  applied_filters: Record<string, string>;
}

interface SummaryPayload {
  total_events: number;
  all_time_total: number;
  by_action: Record<string, number>;
  as_of: string;
}

/** The export row cap the truncation notice references (server constant). */
const EXPORT_ROW_CAP = 50000;
const PAGE_SIZE = 25;

// =============================================================================
// HELPERS
// =============================================================================

const getActionBadgeClass = (action: string): string => {
  if (['login_failed', 'mfa_failed', 'api_error', 'analysis_failed'].includes(action)) return 'action-error';
  if (['login', 'session_created', 'mfa_verified', 'email_verified', 'analysis_completed'].includes(action)) return 'action-success';
  if (['user_deactivated', 'session_revoked', 'all_sessions_revoked', 'journey_deleted'].includes(action)) return 'action-warning';
  return 'action-default';
};

const formatAction = (action: string): string =>
  action.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** "14:22" — the bound rendered in the org timezone. */
function formatBoundTime(asOf: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(asOf));
  } catch {
    return new Date(asOf).toISOString().slice(11, 16);
  }
}

/** "UTC−4" style offset for the caption. */
function formatUtcOffset(asOf: string, timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date(asOf));
    const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
    return name.replace('GMT', 'UTC').replace('-', '−') || 'UTC';
  } catch {
    return 'UTC';
  }
}

function describeFilters(applied: Record<string, string>): string[] {
  const out: string[] = [];
  if (applied.action) out.push(`event type = ${formatAction(applied.action)}`);
  if (applied.resource_type) out.push(`resource = ${applied.resource_type}`);
  if (applied.actor) out.push(`actor matches "${applied.actor}"`);
  if (applied.start_date) out.push(`from ${applied.start_date}`);
  if (applied.end_date) out.push(`to ${applied.end_date}`);
  return out;
}

/**
 * The TRACEABLE reference for a failed load: the middleware-issued request
 * ID from the response header (or error-envelope meta). Returns null when
 * the failure produced no server response at all — the UI must then say so
 * rather than invent an identifier no log will ever contain.
 */
function serverReference(
  response: { headers?: { get?: (name: string) => string | null } } | null,
  body?: { meta?: { requestId?: string } },
): string | null {
  const header = response?.headers?.get?.('x-request-id');
  if (header) return header;
  const fromBody = body?.meta?.requestId;
  return fromBody && fromBody !== 'unknown' ? fromBody : null;
}

// =============================================================================
// DETAIL MODAL (unchanged contract)
// =============================================================================

const DetailModal: React.FC<{ entry: AuditLogEntry | null; onClose: () => void }> = ({
  entry,
  onClose,
}) => {
  if (!entry) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content audit-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Audit Log Details</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close details">&times;</button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Event ID</span>
              <span className="detail-value mono">{entry.id}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Timestamp</span>
              <span className="detail-value">{new Date(entry.created_at).toLocaleString()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Action</span>
              <span className={`action-badge ${getActionBadgeClass(entry.action)}`}>
                {formatAction(entry.action)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Actor</span>
              <span className="detail-value">{entry.user_email || entry.user_id || 'System'}</span>
            </div>
            {entry.resource_type && (
              <div className="detail-row">
                <span className="detail-label">Resource Type</span>
                <span className="detail-value">{entry.resource_type}</span>
              </div>
            )}
            {entry.resource_id && (
              <div className="detail-row">
                <span className="detail-label">Resource ID</span>
                <span className="detail-value mono">{entry.resource_id}</span>
              </div>
            )}
            {entry.ip_address && (
              <div className="detail-row">
                <span className="detail-label">IP Address</span>
                <span className="detail-value mono">{entry.ip_address}</span>
              </div>
            )}
            {entry.user_agent && (
              <div className="detail-row">
                <span className="detail-label">User Agent</span>
                <span className="detail-value small">{entry.user_agent}</span>
              </div>
            )}
            {entry.details && Object.keys(entry.details).length > 0 && (
              <div className="detail-row full-width">
                <span className="detail-label">Additional Details</span>
                <pre className="detail-json">{JSON.stringify(entry.details, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// EXPORT CONFIRMATION DIALOG (C2 modal contract)
// =============================================================================

const ExportDialog: React.FC<{
  boundTime: string;
  matching: number;
  applied: Record<string, string>;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ boundTime, matching, applied, onConfirm, onCancel }) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Tab') {
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === headingRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const exported = Math.min(matching, EXPORT_ROW_CAP);
  const truncated = matching > EXPORT_ROW_CAP;
  const filterList = describeFilters(applied);

  return (
    <div className="modal-backdrop" onKeyDown={onKeyDown} data-testid="export-dialog-backdrop">
      <div
        className="modal-content audit-export-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-export-title"
        ref={dialogRef}
      >
        <h2 id="audit-export-title" tabIndex={-1} ref={headingRef}>
          Export audit events
        </h2>
        <p>
          The export uses the same bound and the same applied filters as the table you are
          looking at.
        </p>
        <dl className="audit-export-facts">
          <div><dt>Bound</dt><dd>{boundTime}</dd></div>
          <div><dt>Matching</dt><dd>{matching.toLocaleString()}</dd></div>
          <div><dt>Exported</dt><dd>{exported.toLocaleString()}</dd></div>
          <div>
            <dt>Truncated</dt>
            <dd>
              {truncated
                ? 'Yes — the file will carry a truncation flag'
                : 'No — the file contains every matching event'}
            </dd>
          </div>
          <div>
            <dt>Applied filters</dt>
            <dd>{filterList.length > 0 ? filterList.join('; ') : 'none'}</dd>
          </div>
        </dl>
        <p className="audit-export-footnote">
          This export is itself recorded as audit_exported, written after the bound, and
          therefore not present in the file it produced. The file header states this.
        </p>
        <div className="audit-export-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConfirm}
            data-testid="export-confirm"
          >
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

type Phase = 'loading' | 'ready' | 'error';

/**
 * One COHERENT snapshot (round-5 P0): rows, counts, bound, timezone,
 * applied-filter echo, and cursor commit TOGETHER — the screen can never
 * show new rows beside stale totals, or a new bound over old counts. While
 * a refresh is in flight the previous coherent snapshot stays visible and
 * Export is disabled.
 */
interface Snapshot {
  entries: AuditLogEntry[];
  /** null = counts unavailable (the G0 partial state for THIS snapshot). */
  total: number | null;
  allTimeTotal: number | null;
  countsUnavailable: boolean;
  asOf: string;
  tzInfo: TimezoneInfo;
  appliedEcho: Record<string, string>;
  nextCursor: Cursor | null;
}

export const AuditLogViewer: React.FC = () => {
  const { authFetch } = useAuthenticatedFetch();

  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  /** Middleware-issued request ID of a failed load; null when the failure
   *  produced no server response (nothing traceable exists — say so). */
  const [errorRef, setErrorRef] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<Cursor[]>([]);
  const [currentCursor, setCurrentCursor] = useState<Cursor | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const [filters, setFilters] = useState<AuditLogFilter>({});
  const [tempFilters, setTempFilters] = useState<AuditLogFilter>({});
  const dirty = JSON.stringify(filters) !== JSON.stringify(tempFilters);

  // Metadata-driven filter options — the taxonomy is never hardcoded here.
  const [actionOptions, setActionOptions] = useState<string[]>([]);
  const [resourceOptions, setResourceOptions] = useState<string[]>([]);

  const applyButtonRef = useRef<HTMLButtonElement | null>(null);
  const exportButtonRef = useRef<HTMLButtonElement | null>(null);
  // Monotonic request sequence: a resolving fetch may only commit state if
  // it is still the LATEST request (round-5 P0: out-of-order responses are
  // rejected, not merged).
  const seqRef = useRef(0);
  const snapRef = useRef<Snapshot | null>(null);
  snapRef.current = snap;

  const filterParams = useCallback((f: AuditLogFilter): URLSearchParams => {
    const params = new URLSearchParams();
    if (f.action) params.append('action', f.action);
    if (f.resource_type) params.append('resource_type', f.resource_type);
    if (f.actor) params.append('actor', f.actor);
    if (f.start_date) params.append('start_date', f.start_date);
    if (f.end_date) params.append('end_date', f.end_date);
    return params;
  }, []);

  /**
   * Load one coherent snapshot. `fresh` mints a NEW bound (the server's
   * database clock) and resets pagination; paging threads the existing
   * bound + cursor. The logs page and its summary are fetched as ONE unit
   * and committed in ONE state swap — never rows now, totals later.
   */
  const fetchPage = useCallback(
    async (opts: { fresh: boolean; cursor?: Cursor | null; filtersOverride?: AuditLogFilter }) => {
      const f = opts.filtersOverride ?? filters;
      const seq = ++seqRef.current;
      setPhase('loading');
      try {
        const params = filterParams(f);
        params.append('limit', String(PAGE_SIZE));
        const priorBound = snapRef.current?.asOf ?? null;
        if (!opts.fresh && priorBound) params.append('as_of', priorBound);
        if (!opts.fresh && opts.cursor) {
          // The cursor timestamp is an OPAQUE server string (lossless
          // microseconds) — threaded back verbatim, never Date-parsed.
          params.append('cursor_created_at', opts.cursor.created_at);
          params.append('cursor_id', opts.cursor.id);
        }
        const logsRes = await authFetch(`/api/audit/logs?${params.toString()}`);
        if (seqRef.current !== seq) return; // superseded — reject stale
        if (!logsRes.ok) {
          let errBody: { meta?: { requestId?: string } } | undefined;
          try {
            errBody = await logsRes.json();
          } catch {
            /* non-JSON error body */
          }
          if (seqRef.current !== seq) return;
          setErrorRef(serverReference(logsRes, errBody));
          setPhase('error');
          return;
        }
        const body = await logsRes.json();
        const data = body.data as LogsPayload;

        // The summary is part of the SAME snapshot: same bound, EVERY
        // applied filter. Its failure yields the counts-unavailable state
        // for this snapshot — never stale totals from the previous one.
        const summaryParams = filterParams(f);
        summaryParams.append('as_of', data.as_of);
        let total: number | null = null;
        let allTimeTotal: number | null = null;
        let countsUnavailable = false;
        try {
          const summaryRes = await authFetch(`/api/audit/summary?${summaryParams.toString()}`);
          if (seqRef.current !== seq) return;
          if (!summaryRes.ok) throw new Error(`summary ${summaryRes.status}`);
          const summaryBody = await summaryRes.json();
          const summary = summaryBody.data.summary as SummaryPayload;
          total = summary.total_events;
          allTimeTotal = summary.all_time_total;
        } catch {
          if (seqRef.current !== seq) return;
          countsUnavailable = true;
        }

        // Atomic swap — rows, counts, bound, timezone, filters, cursor.
        setSnap({
          entries: data.entries,
          total,
          allTimeTotal,
          countsUnavailable,
          asOf: data.as_of,
          tzInfo: {
            timezone: data.timezone,
            timezone_source: data.timezone_source,
            timezone_invalid_value: data.timezone_invalid_value,
          },
          appliedEcho: data.applied_filters,
          nextCursor: data.pagination.next_cursor,
        });
        setErrorRef(null);
        setPhase('ready');
      } catch {
        if (seqRef.current !== seq) return;
        // Network-level failure: no server response, so no traceable
        // reference exists — the UI states that instead of inventing one.
        setErrorRef(null);
        setPhase('error');
      }
    },
    [authFetch, filterParams, filters],
  );

  // Initial load + metadata.
  useEffect(() => {
    void fetchPage({ fresh: true });
    void (async () => {
      try {
        const response = await authFetch('/api/audit/metadata');
        if (!response.ok) return;
        const body = await response.json();
        setActionOptions((body.data?.actions as string[]) ?? []);
        setResourceOptions((body.data?.resource_types as string[]) ?? []);
      } catch {
        /* selects degrade to empty option lists; filters still validate server-side */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const applyFilters = () => {
    setFilters(tempFilters);
    setCursorStack([]);
    setCurrentCursor(null);
    void fetchPage({ fresh: true, filtersOverride: tempFilters });
  };

  const clearAllFilters = () => {
    // G0: Clear all resets EVERYTHING including the date range, re-queries,
    // returns focus to Apply, and announces.
    setTempFilters({});
    setFilters({});
    setCursorStack([]);
    setCurrentCursor(null);
    void fetchPage({ fresh: true, filtersOverride: {} });
    applyButtonRef.current?.focus();
    setAnnouncement('All filters cleared, including the date range.');
  };

  const goNext = () => {
    const next = snap?.nextCursor;
    if (!next || phase === 'loading') return;
    setCursorStack((s) => (currentCursor ? [...s, currentCursor] : [...s, { created_at: '', id: '' }]));
    setCurrentCursor(next);
    void fetchPage({ fresh: false, cursor: next });
  };

  const goPrev = () => {
    if (cursorStack.length === 0 || phase === 'loading') return;
    const prior = cursorStack[cursorStack.length - 1];
    const popped = prior.id === '' ? null : prior;
    setCursorStack((s) => s.slice(0, -1));
    setCurrentCursor(popped);
    void fetchPage({ fresh: false, cursor: popped });
  };

  // ---------------------------------------------------------------------------
  // Derived rendering facts — everything reads the ONE coherent snapshot.
  // During a refresh the previous snapshot stays visible (its rows, counts,
  // and bound belong together); Export is disabled until the swap.
  // ---------------------------------------------------------------------------
  const entries = snap?.entries ?? [];
  const total = snap?.total ?? null;
  const allTimeTotal = snap?.allTimeTotal ?? null;
  const asOf = snap?.asOf ?? null;
  const tzInfo = snap?.tzInfo ?? null;
  const appliedEcho = snap?.appliedEcho ?? {};
  const timezone = tzInfo?.timezone ?? 'UTC';
  const boundTime = asOf ? formatBoundTime(asOf, timezone) : '—';
  const utcOffset = asOf ? formatUtcOffset(asOf, timezone) : 'UTC';
  const filterList = describeFilters(appliedEcho);
  const isLoading = phase === 'loading';
  const isError = phase === 'error';
  const countsUnavailable = (snap?.countsUnavailable ?? false) && !isError;
  const isEmpty = phase === 'ready' && entries.length === 0;
  const truncatedSet = total !== null && total > EXPORT_ROW_CAP;

  const runExport = async () => {
    setExportOpen(false);
    exportButtonRef.current?.focus?.();
    try {
      const params = filterParams(filters);
      params.append('format', 'csv');
      if (asOf) params.append('as_of', asOf);
      const response = await authFetch(`/api/audit/export?${params.toString()}`);
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disposition = response.headers.get('content-disposition');
      const match = disposition?.match(/filename="?([^";]+)"?/);
      a.href = url;
      a.download = match?.[1] ?? 'audit_logs.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setAnnouncement(
        `Export started. The file uses the ${boundTime} bound and the applied filters, and is recorded as audit_exported.`,
      );
    } catch {
      setAnnouncement('Export failed. The audit log is unchanged — try again.');
    }
  };

  // Export disabled + the reason on the ACCESSIBLE NAME (G0 §8.3).
  let exportDisabledReason: string | null = null;
  if (isLoading) exportDisabledReason = 'events are still loading';
  else if (isError) exportDisabledReason = 'the audit log could not be loaded';
  else if (countsUnavailable)
    exportDisabledReason = 'counts are unavailable, so an export could not state its own totals';
  else if (isEmpty) exportDisabledReason = 'no events match the applied filters';

  const showCount = (value: number | null): string =>
    countsUnavailable || isError || value === null ? '—' : value.toLocaleString();

  const captionBase = `Counts as of ${boundTime} · org timezone ${timezone} (${utcOffset})`;
  const caption =
    tzInfo?.timezone_source === 'invalid_setting'
      ? `${captionBase} — configured timezone "${tzInfo.timezone_invalid_value}" is invalid, using UTC`
      : captionBase;

  const appliedBar =
    filterList.length > 0
      ? `Applied filters: ${filterList.join('; ')}.`
      : `Applied filters: none. Showing all events under the ${boundTime} bound.`;
  const appliedBarFull = dirty
    ? `${appliedBar} Filter fields have been edited but not applied — the counts below still reflect the applied set.`
    : appliedBar;

  const tableCaption =
    filterList.length > 0
      ? `Audit events, newest first, as of ${boundTime}, filtered by ${filterList.join('; ')}. Every column reflects the same bound and the same applied filters.`
      : `Audit events, newest first, as of ${boundTime}. All columns reflect the same bound.`;

  return (
    <div className="audit-log-viewer">
      <div className="audit-header">
        <div className="audit-title-section">
          <h1>Audit Log</h1>
          <p className="audit-subtitle" data-testid="audit-caption">{caption}</p>
        </div>
        <div className="audit-actions">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={exportDisabledReason !== null}
            aria-label={
              exportDisabledReason
                ? `Export unavailable — ${exportDisabledReason}`
                : 'Export audit events'
            }
            onClick={() => setExportOpen(true)}
            data-testid="export-open"
            ref={exportButtonRef}
          >
            Export
          </button>
        </div>
      </div>

      {/* Four stat tiles (G0 §8) — the Bound tile is never blanked. */}
      <div className="audit-stat-tiles" data-testid="audit-tiles">
        <div className="audit-stat-tile">
          <span className="audit-stat-label">Total events (all time)</span>
          <span className="audit-stat-value" data-testid="tile-all-time">{showCount(allTimeTotal)}</span>
        </div>
        <div className="audit-stat-tile audit-stat-tile--accent">
          <span className="audit-stat-label">Matching applied filters</span>
          <span className="audit-stat-value" data-testid="tile-matching">{showCount(total)}</span>
        </div>
        <div className="audit-stat-tile">
          <span className="audit-stat-label">Shown in table</span>
          <span className="audit-stat-value" data-testid="tile-shown">
            {isError ? '—' : entries.length}
          </span>
        </div>
        <div className="audit-stat-tile">
          <span className="audit-stat-label">Bound</span>
          <span className="audit-stat-value" data-testid="tile-bound">{boundTime}</span>
        </div>
      </div>

      {/* Counts-unavailable notice (exact operator copy first). */}
      {countsUnavailable && (
        <div className="audit-notice audit-notice--warn" role="note" data-testid="counts-unavailable">
          <strong>Audit data is unavailable. Counts and export are disabled.</strong> The
          aggregate count did not finish, so every total above reads — rather than a number
          that might be wrong. The rows below are correct for this page and this bound.
        </div>
      )}

      {/* Truncation notice. */}
      {truncatedSet && !countsUnavailable && (
        <div className="audit-notice audit-notice--warn" role="note" data-testid="truncation-notice">
          <strong>
            Showing first {EXPORT_ROW_CAP.toLocaleString()} of {total?.toLocaleString()} matching
            events.
          </strong>{' '}
          Refine the filters to narrow the set. An export taken now would contain{' '}
          {EXPORT_ROW_CAP.toLocaleString()} rows and would be marked truncated, carrying the date
          range of the rows it actually contains.
        </div>
      )}

      {/* Filters — draft/applied split; the bar states the applied set. */}
      <div className="audit-filters">
        <select
          value={tempFilters.action ?? ''}
          onChange={(e) => setTempFilters({ ...tempFilters, action: e.target.value || undefined })}
          aria-label="Event type"
        >
          <option value="">All types</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>
              {formatAction(a)}
            </option>
          ))}
        </select>
        <select
          value={tempFilters.resource_type ?? ''}
          onChange={(e) =>
            setTempFilters({ ...tempFilters, resource_type: e.target.value || undefined })
          }
          aria-label="Resource type"
        >
          <option value="">All resources</option>
          {resourceOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={tempFilters.actor ?? ''}
          onChange={(e) => setTempFilters({ ...tempFilters, actor: e.target.value || undefined })}
          aria-label="Actor (name or ID)"
          placeholder="Actor name or ID"
          data-testid="actor-filter"
        />
        <input
          type="date"
          value={tempFilters.start_date ?? ''}
          onChange={(e) =>
            setTempFilters({ ...tempFilters, start_date: e.target.value || undefined })
          }
          aria-label="From date"
        />
        <input
          type="date"
          value={tempFilters.end_date ?? ''}
          onChange={(e) => setTempFilters({ ...tempFilters, end_date: e.target.value || undefined })}
          aria-label="To date"
        />
        <button type="button" className="btn btn-primary" onClick={applyFilters} ref={applyButtonRef}>
          Apply filters
        </button>
        <button type="button" className="btn btn-ghost" onClick={clearAllFilters} data-testid="clear-all">
          Clear all
        </button>
      </div>

      <p className="audit-applied-bar" data-testid="applied-filters-bar">
        {appliedBarFull}
      </p>

      {/* Error state (logs failed). */}
      {isError && (
        <div className="audit-statebox audit-statebox--error" role="alert" data-testid="logs-error">
          <h3>Could not load the audit log</h3>
          <p>
            {errorRef
              ? `The request failed (server reference ${errorRef} — quote this when reporting the issue).`
              : 'The request failed and no server reference was returned — the server may be unreachable.'}{' '}
            No counts are shown, because a partial count is worse than none on a reconciliation
            surface. Export is disabled.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => void fetchPage({ fresh: true })}>
            Retry
          </button>
        </div>
      )}

      {/* Empty state — the log is NOT empty; the total is named. */}
      {isEmpty && !isError && (
        <div className="audit-statebox" data-testid="logs-empty">
          <h3>No events match the applied filters</h3>
          <p>
            The log is not empty
            {allTimeTotal !== null
              ? ` — ${allTimeTotal.toLocaleString()} events exist outside the applied filter set`
              : ''}
            {filterList.length > 0 ? ` (${filterList.join('; ')})` : ''}.
          </p>
          <button type="button" className="btn btn-ghost" onClick={clearAllFilters}>
            Clear all filters
          </button>
        </div>
      )}

      {/* Table. */}
      {!isError && !isEmpty && (
        <div className="audit-table-container">
          <table className="audit-table">
            <caption className="audit-table-caption" data-testid="table-caption">
              {tableCaption}
            </caption>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Resource</th>
                <th>IP Address</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="audit-row loading">
                      <td colSpan={6}>
                        <div className="skeleton skeleton-row" />
                      </td>
                    </tr>
                  ))
                : entries.map((entry) => (
                    <tr key={entry.id} className="audit-row">
                      <td>{new Date(entry.created_at).toLocaleString()}</td>
                      <td className="action-cell">
                        <span className={`action-badge ${getActionBadgeClass(entry.action)}`}>
                          {formatAction(entry.action)}
                        </span>
                      </td>
                      <td className="actor-cell">
                        <span className="actor-email">
                          {entry.user_email || entry.user_id || 'System'}
                        </span>
                      </td>
                      <td>
                        {entry.resource_type
                          ? `${entry.resource_type}${entry.resource_id ? ` (${entry.resource_id.slice(0, 8)}…)` : ''}`
                          : '—'}
                      </td>
                      <td className="mono">{entry.ip_address || '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => setSelectedEntry(entry)}
                          aria-label={`View details for ${formatAction(entry.action)} at ${new Date(entry.created_at).toLocaleString()}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>

          {/* Keyset pagination: Prev replays the cursor stack. */}
          <div className="audit-pagination" data-testid="audit-pagination">
            <button
              type="button"
              className="btn-pagination"
              onClick={goPrev}
              disabled={cursorStack.length === 0 || isLoading}
            >
              Previous
            </button>
            <span className="audit-pagination-caption">
              Showing {entries.length} of {showCount(total)} matching events
            </span>
            <button
              type="button"
              className="btn-pagination"
              onClick={goNext}
              disabled={!snap?.nextCursor || isLoading}
              data-testid="page-next"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {exportOpen && total !== null && (
        <ExportDialog
          boundTime={boundTime}
          matching={total}
          applied={appliedEcho}
          onCancel={() => {
            setExportOpen(false);
            exportButtonRef.current?.focus?.();
          }}
          onConfirm={() => void runExport()}
        />
      )}

      <DetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />

      {/* No toasts — announcements are polite live-region strings. */}
      <div aria-live="polite" role="status" className="audit-visually-hidden" data-testid="audit-announcement">
        {announcement}
      </div>
    </div>
  );
};

export default AuditLogViewer;
